import {bootstrapApplication} from '@angular/platform-browser';
import {AppComponent} from './app/app.component';
import {
  HttpClient,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
  provideHttpClient,
  withInterceptors
} from '@angular/common/http';
import {provideRouter, withDebugTracing} from '@angular/router';
import {provideAnimations} from '@angular/platform-browser/animations';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {environment} from '@env/environment';
import {LanguageFactory} from '@shared/languages/language-factory';
import {importProvidersFrom, inject, provideAppInitializer} from '@angular/core';
import {APP_BASE_HREF} from '@angular/common';
import {getBaseLocation} from '@shared/common-functions.util';
import {provideEnvironmentNgxMask} from 'ngx-mask';
import {MatNativeDateModule} from '@angular/material/core';
import {NgSelectModule} from '@ng-select/ng-select';
import {ModalModule} from 'ngx-bootstrap/modal';
import {routes} from './app/layout/routes';
import {catchError, filter, firstValueFrom, NEVER, of, throwError, timeout} from 'rxjs';
import {AuthenticationResourceFilter} from '@auth/authentication-resource.filter';
import {AuthenticationService} from '@auth/authentication.service';
import {JwtAuthenticationService} from '@auth/jwt/jwt-authentication.service';
import {LocalStorageService} from '@services/local-storage.service';
import {ClientManagementService} from '@services/client-management.service';
import {LS_DATE_PIPE_PROVIDER} from '@common/time/ls-date.pipe';
import {PermissionsService} from './app/pages/permissions/permissions.service';
import {
  changedClientLogin,
  ClientLoginChangeService,
  LOGIN_CHANGE_AWARE_HEADER,
  LOGIN_CHANGE_AWARE_VALUE
} from '@services/client-login-change.service';

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(
      withInterceptors([interceptor])
    ),
    provideRouter(
      routes,
      // withDebugTracing(),
    ),
    provideAnimations(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: environment.DEFAULT_LANG,
        loader: {
          provide: TranslateLoader,
          useFactory: LanguageFactory,
          deps: [HttpClient]
        },
      }),
      MatNativeDateModule,
      NgSelectModule,
      ModalModule.forRoot(),
    ),
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseLocation
    },
    {
      provide: AuthenticationService,
      useClass: JwtAuthenticationService
    },
    provideEnvironmentNgxMask(),
    // Render every date/time in the app time zone (see APP_TIME_ZONE) app-wide.
    // This rebinds DatePipe, so all injected `datePipe.transform(...)` call sites
    // are covered automatically; templates use the `| lsDate` pipe.
    LS_DATE_PIPE_PROVIDER,
    // note: the boot loader in index.html is removed once bootstrap resolves,
    // which happens after the app initializer below completes.
    // Warm up the client list cache once, before the app bootstraps, so that
    // route guards can resolve the selected client synchronously from cached data.
    provideAppInitializer(() => {
      const localStorageService = inject(LocalStorageService);
      const clientManagementService = inject(ClientManagementService);
      // only fetch for authenticated users — anonymous requests would 401/redirect
      if (!localStorageService.isAuthenticated()) {
        return;
      }
      // never block bootstrap indefinitely: the 401 interceptor returns NEVER,
      // so cap the wait and swallow any failure.
      return firstValueFrom(
        clientManagementService.get().pipe(
          timeout(8000),
          catchError(() => of(null)),
        )
      );
    }),
    // Permission grants are otherwise only applied at sign-in, and sessions stay alive
    // indefinitely — so refresh the cached permission zones on every app start too,
    // not just at login, or a grant/revoke never reaches an already-signed-in user.
    provideAppInitializer(() => {
      const localStorageService = inject(LocalStorageService);
      const permissionsService = inject(PermissionsService);
      if (!localStorageService.isAuthenticated()) {
        return;
      }
      return firstValueFrom(
        permissionsService.getMine().pipe(
          timeout(8000),
          catchError(() => of(null)),
        )
      ).then((permissions) => {
        if (permissions) {
          localStorageService.setPermissions(permissions);
        }
      });
    }),
  ]
})
  .catch(err => {
    console.error(err);
    // make sure the boot loader never gets stuck if bootstrap fails
    document.getElementById('app-boot-loader')?.remove();
  });

/** Pages opened from an emailed link by someone who can't sign in yet. */
const SIGNED_OUT_LINK_PAGES = ['/password/setup', '/recovery-password'];

function interceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authenticationResourceFilter = inject(AuthenticationResourceFilter);
  const localStorageService = inject(LocalStorageService);
  const clientLoginChangeService = inject(ClientLoginChangeService);

  if (req.url.startsWith(environment.API_URL)) {
    req = req.clone({setHeaders: {[LOGIN_CHANGE_AWARE_HEADER]: LOGIN_CHANGE_AWARE_VALUE}});
  }

  if (authenticationResourceFilter.isAuthenticationRequired(req.url)) {
    return next(req)
    .pipe(
      filter(event => event instanceof HttpResponse),
      catchError((error) => {
        if (error.status === 401) {
          localStorageService.clearStorage();
          // Card #1640: a set-password or reset link opened in a browser holding an expired sign-in gets 401 from
          // the startup calls above. Clearing that sign-in is enough there: the page then opens signed out.
          // Sending the person to sign in would leave them on a login form for a password they don't have yet.
          if (SIGNED_OUT_LINK_PAGES.some(page => window.location.pathname.endsWith(page))) {
            return throwError(error);
          }
          // Get current URL with query parameters and add as ref parameter
          const currentUrl = window.location.pathname + window.location.search;
          const returnUrl = encodeURIComponent(currentUrl);
          window.location.href = `/login?ref=${returnUrl}`;
          return NEVER;
        }
        // Card #1479: the client named in the call has a new login. Recover without showing an error.
        const newLogin = changedClientLogin(error);
        if (newLogin && clientLoginChangeService.recover(req, newLogin)) {
          return NEVER;
        }
        return throwError(error);
      }),
    );
  } else {
    return next(req);
  }
}
